import { Dispatch, SetStateAction, useState } from 'react';

import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import { UsersMerchantAccessDialog } from 'pages/Users/components/UsersMerchantAccessDialog';
import { useUserMerchantAccessPermission } from 'pages/Users/hooks/useUserMerchantAccessPermission';
import { required, useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { Checkbox } from 'shared/mui/Checkbox/Checkbox';
import { TextField } from 'shared/mui/Fields';
import { FormControlLabel } from 'shared/mui/FormControlLabel/FormControlLabel';
import { TooltipWithInfoIcon } from 'shared/mui/Tooltip';
import { AutocompleteInput, BooleanInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { User } from 'types/embedded/user';

import { AutocompleteStyled, ButtonStyled, BoxStyled, ChipStyled } from './UsersEditForm.styled';
import { useGetUsersMerchants } from '../hooks/useGetUsersMerchants';

type UsersFormProps = {
  hasCorporateEmail?: boolean;
  setHasCorporateEmail: Dispatch<SetStateAction<boolean>>;
  isEditForm?: boolean;
};

export const UsersEditForm: React.FC<UsersFormProps> = ({
  hasCorporateEmail,
  setHasCorporateEmail,
}) => {
  const record = useRecordContext<User>();
  const isUserMerchantAccessAvailable = useUserMerchantAccessPermission(record?.rolesValues);
  const merchantAccessesIds = record?.merchantAccesses?.map(
    (merchantAccess) => merchantAccess?.['@id'],
  );
  const { merchants } = useGetUsersMerchants(merchantAccessesIds);

  const [isOpenMerchantAccessDialog, setIsOpenMerchantAccessDialog] = useState(false);

  return (
    <BoxStyled>
      <BooleanInput
        defaultValue={true}
        label="Активен"
        name="enabled"
        size="small"
        source="enabled"
        sx={{ marginBottom: '24px', gridColumn: ' 1 / span 2' }}
        variant="outlined"
      />
      <TextInput
        fullWidth
        label="Имя"
        size="small"
        source="firstName"
        validate={required()}
        variant="outlined"
      />
      <TextInput
        fullWidth
        label="Фамилия"
        size="small"
        source="lastName"
        validate={required()}
        variant="outlined"
      />
      <TextInput
        fullWidth
        label="Reddy"
        size="small"
        source="reddyNumber"
        validate={required()}
        variant="outlined"
      />
      <TextInput
        fullWidth
        label="Электронная почта"
        size="small"
        source="email"
        variant="outlined"
      />
      <ReferenceInput perPage={200} reference="roles" source="roleEntities">
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          format={(v) => v?.[0] || null}
          fullWidth
          label="Роль"
          optionText="name"
          parse={(v) => (v ? [v] : [])}
          size="small"
          source="roleEntities"
          sx={{ marginBottom: '4px', gridColumn: '1 / span 2' }}
          validate={required()}
          variant="outlined"
        />
      </ReferenceInput>

      <AutocompleteStyled
        clearIcon={false}
        disabled
        multiple
        options={merchants || []}
        renderInput={(params) => (
          <TextField
            {...params}
            InputLabelProps={{ shrink: true }}
            label="Доступ к мерчантам"
            size="small"
            variant="outlined"
          />
        )}
        renderTags={(merchants) =>
          merchants?.map((merchant) => (
            <ChipStyled disabled key={merchant?.id} label={merchant?.name} size="small" />
          ))
        }
        size="small"
        sx={{ marginBottom: '18px', gridColumn: ' 1 / span 2' }}
        value={merchants || []}
      />
      <ButtonStyled
        disabled={!isUserMerchantAccessAvailable}
        fullWidth
        label="Настроить доступ к мерчантам"
        onClick={() => setIsOpenMerchantAccessDialog(true)}
        startIcon={isUserMerchantAccessAvailable ? false : <DoDisturbIcon color="disabled" />}
        sx={{ gridColumn: ' 1 / span 2' }}
        variant="outlined"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={hasCorporateEmail}
            onChange={() => setHasCorporateEmail(!hasCorporateEmail)}
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            Групповой email
            <TooltipWithInfoIcon
              componentsProps={{ popper: { sx: { width: '247px' } } }}
              iconSize={24}
              tooltipText="Используется для отправки отчётов разных пользователей на один email."
            />
          </Box>
        }
        sx={{ marginBottom: '20px', gridColumn: ' 1 / span 2' }}
      />
      {hasCorporateEmail && (
        <TextInput
          fullWidth
          label="Групповой email"
          name="corporateEmail"
          size="small"
          source="corporateEmail"
          sx={{ gridColumn: ' 1 / span 2' }}
          variant="outlined"
        />
      )}
      {isOpenMerchantAccessDialog && (
        <UsersMerchantAccessDialog
          onClose={() => setIsOpenMerchantAccessDialog(false)}
          open={isOpenMerchantAccessDialog}
          record={record}
        />
      )}
    </BoxStyled>
  );
};
